import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  useCreateVignetteRange,
  useGetVignetteRangeDetails,
  useGetVignetteRangeStatusLOV,
  useUpdateVignetteRange
} from '@common/slices';
import { EnhancedButtonStatus } from '@components/button/EnhancedButton';
import DynamicFormDrawer from '@components/dynamic-form/DynamicFormDrawer';
import { IDynamicFormWidgetSection } from '@components/dynamic-form/types';
import { ISelectFormFieldProps } from '@components/dynamic-form/types/inputTypes';
import { normalizeDynamicFormDrawerValues } from '@components/dynamic-form/utils';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import { DEFAULT_ERROR_TEXT } from '@utils';
import { enumListAsRecordObject } from '@utils/graph-utils';

import { vignetteRangeDrawerSections } from '../constants';

import { cloneDeep } from 'lodash';

interface IVignetteRangeDrawer {
  open: boolean;
  onClose: () => void;
  vignetteRangeId: string;
}

const VignetteRangeDrawer: React.FC<IVignetteRangeDrawer> = ({
  open,
  onClose,
  vignetteRangeId
}) => {
  const params = useParams();

  const [booted, setBooted] = useState(false);
  const [formDisabled, setFormDisabled] = useState(false);
  const [submitButtonState, setSubmitButtonState] =
    useState<EnhancedButtonStatus>();
  const [inputsForm, setInputsForm] = useState<IDynamicFormWidgetSection[]>(
    vignetteRangeDrawerSections
  );

  const [updateVignetteRange] = useUpdateVignetteRange();
  const [createVignetteRange] = useCreateVignetteRange(params.id);

  const { data: vigenetteDetails } = useGetVignetteRangeDetails({
    variables: {
      id: vignetteRangeId
    }
  });
  const { data: vigenetteStatusLOVs } = useGetVignetteRangeStatusLOV();

  const refineDetailsData = () => {
    const details =
      vigenetteDetails?.salesforce?.getBusinessPartnerVignetteRangeById;
    const lovs = vigenetteStatusLOVs?.VignetteStatus;

    return {
      vigDetails: {
        rangeCode: details?.vignetteCode,
        rangeYear: details?.vignetteYear,
        rangeFrom: details?.rangeFrom,
        rangeTo: details?.rangeTo,
        rangeStatus: details?.status
      },
      statusLOVs: {
        [lovs?.name]: enumListAsRecordObject(lovs?.enumValues)
      }
    };
  };

  useEffect(() => {
    initialize();
  }, []);

  const initialize = async () => {
    try {
      const updatedSections = cloneDeep(inputsForm);
      const updatedInputs = updatedSections[0].inputs;
      const [entityData] = await Promise.all([refineDetailsData()]);

      (updatedInputs.rangeStatus as ISelectFormFieldProps).selectOptions =
        entityData?.statusLOVs?.['VignetteStatus'];

      if (vignetteRangeId) {
        updatedInputs.rangeYear.value = entityData?.vigDetails?.rangeYear;
        updatedInputs.rangeCode.value = entityData?.vigDetails?.rangeCode;
        updatedInputs.rangeFrom.value = entityData?.vigDetails?.rangeFrom;
        updatedInputs.rangeTo.value = entityData?.vigDetails?.rangeTo;
        updatedInputs.rangeStatus.value = entityData?.vigDetails?.rangeStatus;
        setInputsForm(updatedSections);
      }
      setBooted(true);
    } catch (err) {
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  const submitForm = async (values: Record<string, any>) => {
    const [data] = normalizeDynamicFormDrawerValues(
      vignetteRangeDrawerSections,
      values
    );
    setFormDisabled(true);
    setSubmitButtonState('loading');

    try {
      const input = {
        businessPartnerId: params.id,
        code: data.rangeCode,
        year: parseInt(data.rangeYear),
        rangeFrom: parseInt(data.rangeFrom),
        rangeTo: parseInt(data.rangeTo),
        status: data.rangeStatus
      };
      if (vignetteRangeId)
        updateVignetteRange({
          modifyBusinessPartnerVignetteInput: {
            ...input,
            id: vignetteRangeId
          }
        });
      else
        createVignetteRange({
          createBusinessPartnerVignetteInput: input
        });
      setSubmitButtonState('success');
      setTimeout(() => {
        (onClose(), setSubmitButtonState(undefined));
      }, 500);
    } catch (err) {
      setSubmitButtonState(undefined);
      setFormDisabled(false);
      toast.error(<ToastErrorMessage>{DEFAULT_ERROR_TEXT}</ToastErrorMessage>);
    }
  };

  return (
    <DynamicFormDrawer
      title={vignetteRangeId ? 'Edit Vignette Range' : 'New Vignette Range'}
      onClose={onClose}
      isOpen={open}
      loading={!booted}
      sections={inputsForm}
      onSubmit={(values) => submitForm(values)}
      submitButtonState={submitButtonState}
      disableForm={formDisabled}
    />
  );
};

export { VignetteRangeDrawer };
